import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import TabSettings from "components/__new/common/TabSettings";
import Maybe from "components/__new/common/Maybe";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import { BackButtonPage } from "components/__new/common/BackButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DateFormat, { monthNames } from "utils/dateFormat";
import { renderStatusColor, renderStatusText } from "utils/payroll/statusUI";
import useLoginInfo from "hooks/useLoginInfo";
import NumberFormat from "utils/numberFormat";
import CurrencyFormat from "utils/currencyFormat";
import PageContent, { PageContentCustom } from "components/__new/common/PageContent";
import { ReactComponent as IconWallet } from "assets/images/icons/project-icons/wallet-01.svg";
import RequestAccessPin from "components/__new/common/access-pin/RequestAccessPin";
// import validate from "utils/validate";;
import { toast } from "react-toastify";
import ModalAlert from "components/__new/common/ModalAlert";
import transformRemittanceName from "data/remittanceNames";
import Environment from "components/__new/common/Environment";
import { TransactionsListReadOnly } from "components/__new/payroll/runPayroll/TransactionsList-ReadOnly";
import * as payrollAction from "store/entities/payroll/runPayroll/action";
import * as walletAction from "store/entities/wallet/action";


const Index = () => {
    const {id, remittanceId} = useParams();
    const dispatch = useDispatch();
    const {userInfo} = useLoginInfo();


    const {
        payrollLoading, payroll,
        payrollRemittancesLoading, payrollRemittances,
        payrollRemittancePaymentLoading, payrollRemittancePayment,
    } = useSelector((s) => s.entities.runPayroll);
    const { dashboardLoading: walletDashboardLoading, dashboard: walletDashboard } = useSelector((s) => s.entities.wallet);


    const [isOpenAccessPin, setIsOpenAccessPin] = useState(false);
    const [isOpenModalAlertPayRemittanceSuccess, setIsOpenModalAlertPayRemittanceSuccess] = useState(false);
    const [form, setForm] = useState({
        accessPin: "",
    });
    const [payrollCharge, setPayrollCharge] = useState(null);
    const [payrollRemittance, setPayrollRemittance] = useState({});
    const [payrollRemittancePaymentMessage, setPayrollRemittancePaymentMessage] = useState({});

    
    const getPayrollDetails = () => {
        dispatch(payrollAction.getPayrollDetails(id));
    }


    const getWalletDetails = () => {
        if (walletDashboardLoading === false && (walletDashboard?.status === undefined || walletDashboard?.status === "failed")){
            dispatch(walletAction.getDashboardSilent(userInfo.token));
        }
    }


    const getRemittances = () => {
        if (payrollRemittancesLoading === false && payrollRemittances?.length === 0){
            dispatch(payrollAction.getRemittances(id));
        }
    }


    const resetPayRemittance = () => {
        dispatch(payrollAction.resetPayRemittance());
    }


    const submitForm = async (e) => {
        e.preventDefault();

        if (payrollCharge === null){
            toast.error("An error occurred. Please try again later");
        }
        else if (payrollCharge > (walletDashboard?.data?.walletDetails?.balance / 100)){
            // toast.error("To proceed, please fund your wallet");
            toast.error("Insufficient wallet balance, please fund your wallet");
        }
        else{
            setIsOpenAccessPin(true);
        }
    }
    

    const processForm = () => {
        const params = {
            id: remittanceId,
            access_pin: form.accessPin,
        }
        dispatch(payrollAction.payRemittance(id, params));
    }


    useEffect(() => {
        if (id){
            getPayrollDetails();
            getWalletDetails();
            getRemittances();
        }
        // eslint-disable-next-line
    }, []);


    useEffect(() => {
        if (payrollRemittancesLoading === false && payrollRemittances?.length > 0){
            const selected = payrollRemittances?.filter((remittance) => remittance.id === remittanceId);
            if (selected.length > 0){
                setPayrollRemittance(selected[0]);
                setPayrollCharge(selected[0]?.metadata?.remittanceDetails?.paymentAmount);
            }
        }
        // eslint-disable-next-line
    }, [payrollRemittances]);
    

    useEffect(() => {
        if (payrollRemittancePayment?.status === "success"){
            setIsOpenAccessPin(false);
            setIsOpenModalAlertPayRemittanceSuccess(true);
            setPayrollRemittancePaymentMessage(payrollRemittancePayment);
            resetPayRemittance();
            getPayrollDetails();
            getWalletDetails();
        }
        // eslint-disable-next-line
    }, [payrollRemittancePayment]);


    return (
        <div>

            <DynamicHeadTag
                headerText={id + " - Review Remittance"}
                headerDescription=""
            />


            <RequestAccessPin
                target="Access Pin"
                title="Validate Transfer"
                description="Enter Access Pin"
                showForgotPin={true}
                footnote={
                    <span className="text-sm text-ep-gray-600">
                        By clicking “Continue” you authorize a deduction of&nbsp;
                        <span className="text-ep-primary font-semibold">
                            {CurrencyFormat(payrollCharge)}
                        </span>
                        &nbsp;from your Wallet Balance
                    </span>
                }
                pin={(e) => setForm({...form, accessPin: e})}
                submit={() => {
                    processForm();
                }}
                loading={payrollRemittancePaymentLoading}
                modalLoading={false}
                isOpenPin={isOpenAccessPin}
                setIsOpenPin={setIsOpenAccessPin}
            />


            <ModalAlert
                successText={payrollRemittancePaymentMessage?.message || "Remittance payment successful"}
                dismissText="Close"
                isOpenModalAlert={isOpenModalAlertPayRemittanceSuccess}
                setIsOpenModalAlert={setIsOpenModalAlertPayRemittanceSuccess}
            />


            <div className="border-b">
                <TabSettings options={[
                    {
                        title: <span>Pay Run</span>,
                        type: "link",
                        url: "/payroll",
                    },
                    {
                        title: <span>Payroll History</span>,
                        type: "link",
                        url: "/payroll/history",
                    },
                ]} />
            </div>


            <div className="mt-6 lg:flex">
                <div className="w-full lg:w-1/2">
                    <div className="page-title flex items-center">
                        <BackButtonPage />
                        
                        <Maybe condition={payrollLoading === true}>
                            <FontAwesomeIcon icon="circle-notch" spin className="text-xl mr-2" /> Payroll
                        </Maybe>

                        <Maybe condition={payrollLoading === false}>
                            <div>
                                {monthNames[payroll?.schedule?.month - 1] || "-"} {payroll?.schedule?.year} Payroll
                            </div>
                            
                            <div className="text-base font-normal hidden lg:block pl-6 ml-6 border-l border-gray-300">
                                Period:
                                <span className="ml-2 text-ep-secondary-600">
                                    {DateFormat(payroll?.payroll_date_range?.start_date) || "-"}
                                    &nbsp;-&nbsp;
                                    {DateFormat(payroll?.payroll_date_range?.end_date) || "-"}
                                </span>
                            </div>
                        </Maybe>
                    </div>
                </div>
                <div className="w-full lg:w-1/2 flex flex-wrap items-center mt-4 lg:mt-0 lg:justify-end">

                    <Maybe condition={payrollLoading === false}>
                        <div className="lg:hidden mr-4">
                            Period:
                            <span className="ml-2 text-ep-secondary-600">
                                {DateFormat(payroll?.payroll_date_range?.start_date) || "-"}
                                &nbsp;-&nbsp;
                                {DateFormat(payroll?.payroll_date_range?.end_date) || "-"}
                            </span>
                        </div>

                        <label className={"label-parent " + renderStatusColor(payroll?.status)}>
                            <label className="label-child">
                                {payroll?.status}
                            </label>
                            <span className="text-ep-primary-700 font-medium">
                                {renderStatusText(payroll?.status)}
                            </span>
                        </label>
                    </Maybe>

                </div>
            </div>


            <PageContent
                loading={payrollLoading}
                data={payroll?.id}
                classes="mt-6 box"
                loadingText="Loading payroll data"
                emptyText="Payroll data not found"
                errorText="Unable to load payroll data"
            />


            <Maybe condition={payroll?.type === "GROSS"}>
                <Environment develop={true} staging={true} production={false}>                        

                    <Maybe condition={payrollLoading === false && payroll?.id !== undefined}>
                        <Maybe condition={!(payroll?.status === "approved" || payroll?.status === "processing" || payroll?.status === "completed")}>
                            <PageContentCustom
                                icon="exclamation-triangle"
                                classes="mt-6 box"
                                text="Access Denied"
                            />
                        </Maybe>

                        <Maybe condition={payroll?.status === "approved" || payroll?.status === "processing" || payroll?.status === "completed"}>
                            <div className="mt-6 py-6 border-t border-gray-200">
                                <form onSubmit={submitForm} className="w-full sm:flex sm:justify-between items-center">
                                    <div>
                                        <span className="font-bold text-xl sm:text-2xl">Review Remittance</span>
                                    </div>
                                    <div>
                                        <button type="submit" className="h-max btn btn-lg btn-ep-primary">
                                            Make Payment
                                        </button>
                                    </div>
                                </form>
                            </div>


                            <div className="mt-0 pt-6 border-t border-gray-200 xl:flex xl:space-x-6">
                                <div className="w-full xl:w-2/3 box">
                                    <div className="font-medium text-lg">
                                        Remittance Details
                                    </div>
                                    <div className="mt-6 pt-3 border-t border-gray-200">

                                        {/* <Maybe condition={payrollRemittancesLoading === true}> */}
                                            <PageContent
                                                loading={payrollRemittancesLoading}
                                                data={payrollRemittances}
                                                loadingText="Loading remittance"
                                                emptyText="Remittance not found"
                                                errorText="Unable to load remittance"
                                            />
                                        {/* </Maybe> */}

                                        <Maybe condition={payrollRemittancesLoading === false && payrollRemittances.length > 0}>
                                            <div className="py-3 flex space-x-4 justify-between">
                                                <div className="text-[#6B7280]">
                                                    Name of remittance party
                                                </div>
                                                <div className="font-semibold">
                                                    {transformRemittanceName(payrollRemittance?.metadata?.remittanceDetails?.nameOfRemittanceParty) || "-"}
                                                </div>
                                            </div>

                                            <div className="py-3 flex space-x-4 justify-between">
                                                <div className="text-[#6B7280]">
                                                    Remittance information
                                                </div>
                                                <div className="font-semibold">
                                                    {payrollRemittance?.metadata?.remittanceDetails?.remittanceInformation || "-"}
                                                </div>
                                            </div>

                                            <Maybe condition={
                                                payrollRemittance?.metadata?.remittanceDetails?.nameOfRemittanceParty?.toLowerCase() !== "pension" &&
                                                payrollRemittance?.metadata?.remittanceDetails?.nameOfRemittanceParty?.toLowerCase() !== "paye_tax"}
                                            >
                                                <div className="py-3 flex space-x-4 justify-between">
                                                    <div className="text-[#6B7280]">
                                                        Account number
                                                    </div>
                                                    <div className="font-semibold">
                                                        {payrollRemittance?.metadata?.remittanceDetails?.accountNumber || "-"}
                                                    </div>
                                                </div>

                                                <div className="py-3 flex space-x-4 justify-between">
                                                    <div className="text-[#6B7280]">
                                                        Bank name
                                                    </div>
                                                    <div className="font-semibold">
                                                        {payrollRemittance?.metadata?.remittanceDetails?.bankName || "-"}
                                                    </div>
                                                </div>

                                                <div className="py-3 flex space-x-4 justify-between">
                                                    <div className="text-[#121213]">
                                                        Account name
                                                    </div>
                                                    <div className="font-semibold">
                                                        {payrollRemittance?.metadata?.remittanceDetails?.accountName || "-"}
                                                    </div>
                                                </div>
                                            </Maybe>

                                            <div className="py-3 flex space-x-4 justify-between">
                                                <div className="text-[#6B7280]">
                                                    Payment amount
                                                </div>
                                                <div className="font-semibold">
                                                    {CurrencyFormat(payrollRemittance?.metadata?.remittanceDetails?.paymentAmount) || "-"}
                                                </div>
                                            </div>

                                            <div className="py-3 flex space-x-4 justify-between">
                                                <div className="text-[#6B7280]">
                                                    Number of employees
                                                </div>
                                                <div className="font-semibold">
                                                    {NumberFormat(payrollRemittance?.metadata?.remittanceDetails?.numberOfEmployees, "no-kobo") || "-"}
                                                </div>
                                            </div>
                                        </Maybe>

                                    </div>
                                </div>


                                <div className="mt-6 xl:mt-0 w-full xl:w-1/3 box">
                                    <div className="font-medium text-lg">
                                        Payment Method
                                    </div>
                                    <div className="mt-6 py-6 border-t border-gray-200">

                                        <PageContent
                                            loading={walletDashboardLoading}
                                            data={walletDashboard}
                                            loadingText="fetching wallet information"
                                            emptyText="Wallet information not found"
                                            errorText="Unable to fetch wallet information"
                                        />
                                        
                                        <Maybe condition={walletDashboardLoading === false && walletDashboard?.status === "success"}>
                                            <div className="text-[#003F46] text-sm font-medium">
                                                Earnipay Wallet <span className="text-[#718096]">(Default)</span>
                                            </div>

                                            <div className="w-full box mt-2 !p-4 border border-gray-200 flex space-x-4 items-center">
                                                <div className="w-10 h-10 bg-ep-primary-100 flex-shrink-0 flex items-center justify-center rounded-lg">
                                                    <IconWallet className="w-5 h-5" />
                                                </div>
                                                <div>
                                                    <div className="text-sm text-[#6B7280]">
                                                        Wallet Balance
                                                    </div>
                                                    <div className="text-ep-primary text-lg font-semibold">
                                                        {CurrencyFormat(walletDashboard?.data?.walletDetails?.balance / 100) || "-"}
                                                    </div>
                                                </div>
                                            </div>

                                            <Maybe condition={payrollCharge !== null && payrollCharge > (walletDashboard?.data?.walletDetails?.balance / 100)}>
                                                <div className="mt-2 text-sm text-[#6B7280]">
                                                    Fund your wallet with&nbsp;
                                                    <span className="text-ep-primary font-semibold">
                                                        {CurrencyFormat(payrollCharge - (walletDashboard?.data?.walletDetails?.balance / 100)) || "-"}
                                                    </span>
                                                    &nbsp;to proceed.
                                                    To fund your wallet use the information below:
                                                </div>
                                            </Maybe>

                                            <div className="mt-4 px-4 py-2 bg-[#F9FAFB] border border-gray-200 rounded-md">
                                                <span className="text-fade mr-2">
                                                    Bank:
                                                </span>
                                                <span className="capitalize">
                                                    {walletDashboard?.data?.walletDetails?.bankName}
                                                </span>
                                            </div>

                                            <div className="mt-4 flex space-x-6 text-sm">
                                                <div className="w-1/2">
                                                    <div className="text-fade text-sm">
                                                        Acc Name:
                                                    </div>
                                                    <div className="mt-2 text-[#374151] font-bold">
                                                        {walletDashboard?.data?.walletDetails?.accountName}
                                                    </div>
                                                </div>
                                                <div className="w-1/2">
                                                    <div className="text-fade text-sm">
                                                        Virtual Account Number:
                                                    </div>
                                                    <div className="mt-2 text-[#374151] font-bold">
                                                        {walletDashboard?.data?.walletDetails?.accountNumber}
                                                    </div>
                                                </div>
                                            </div>
                                        </Maybe>

                                    </div>
                                </div>
                            </div>


                            <div className="mt-6">
                                <TransactionsListReadOnly
                                    payroll={payroll}
                                />
                            </div>
                        </Maybe>
                    </Maybe>
                    
                </Environment>
            </Maybe>
            
            
            <Maybe condition={payroll?.type === "NET"}>
                <PageContentCustom
                    icon="exclamation-triangle"
                    classes="mt-6 box"
                    text="Access Denied"
                />
            </Maybe>

            
            <Environment local={false} develop={false} staging={false} production={true}>
                <PageContentCustom
                    icon="exclamation-triangle"
                    classes="mt-6 box"
                    text="Access Denied"
                />
            </Environment>


        </div>
    )
}


export default Index;